import { useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { fundoReservaService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";
import {
  formatarValorReal,
  formatarPorcentagem,
  formatCurrencyMoeda,
} from "../../utils/FormatarValores";

const TIPOS_APORTE = [
  { value: "PORCENTAGEM", label: "Porcentagem" },
  { value: "VALOR_FIXO", label: "Valor fixo" },
];

const FORM_INICIAL = {
  nomeFundo: "",
  descricao: "",
  valorAporte: 0,
  valorAporteFormatted: "0,00",
  tipoAporte: "",
  ordem: "",
};

const FundoReserva = () => {
  const [fundosReserva, setFundosReserva] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState(FORM_INICIAL);
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });
  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();

  useEffect(() => {
    loadFundosReserva();
  }, []);

  const loadFundosReserva = async () => {
    setLoading(true);
    try {
      const data = await fundoReservaService.listarFundosReserva();
      setFundosReserva(Array.isArray(data) ? data : []);
      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const isPercentual = (tipoAporte) => tipoAporte === "PORCENTAGEM";

  const formatarValorInput = (valor, tipoAporte) => {
    const numero = Number(valor || 0);

    if (isPercentual(tipoAporte)) {
      return formatarPorcentagem((numero * 10).toFixed(0)).formatted;
    }

    return formatarValorReal((numero * 100).toFixed(0)).formatted;
  };

  const formatarTipoAporte = (tipoAporte) => {
    if (!tipoAporte) return "-";

    const tipoEncontrado = TIPOS_APORTE.find((tipo) => tipo.value === tipoAporte);
    if (tipoEncontrado) return tipoEncontrado.label;

    const texto = tipoAporte.toString().replace(/_/g, " ").toLowerCase();
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  };

  const formatarValorAporte = (valorAporte, tipoAporte) => {
    const valorNumerico = Number(valorAporte || 0);

    if (tipoAporte === "PORCENTAGEM") {
      return `${valorNumerico.toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;
    }

    return formatCurrencyMoeda(valorNumerico);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleValorAporteChange = (e) => {
    const { formatted, numeric } =
      isPercentual(formData.tipoAporte)
        ? formatarPorcentagem(e.target.value)
        : formatarValorReal(e.target.value);

    setFormData((prev) => ({
      ...prev,
      valorAporte:
        isPercentual(formData.tipoAporte)
          ? Number(Number(numeric).toFixed(1))
          : numeric,
      valorAporteFormatted: formatted,
    }));
  };

  const handleTipoAporteChange = (e) => {
    const tipoAporte = e.target.value;

    setFormData((prev) => ({
      ...prev,
      tipoAporte,
      valorAporteFormatted: formatarValorInput(prev.valorAporte, tipoAporte),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.nomeFundo.trim()) {
      showError("Nome é obrigatório");
      return;
    }

    if (!formData.tipoAporte.trim()) {
      showError("Tipo de aporte é obrigatório");
      return;
    }

    if (!TIPOS_APORTE.some((tipo) => tipo.value === formData.tipoAporte)) {
      showError("Tipo de aporte inválido");
      return;
    }

    if (
      formData.ordem !== "" &&
      (!Number.isInteger(Number(formData.ordem)) || Number(formData.ordem) < 0)
    ) {
      showError("Ordem deve ser um número inteiro maior ou igual a zero");
      return;
    }

    setLoading(true);

    try {
      const valorAporteNormalizado =
        isPercentual(formData.tipoAporte)
          ? Number(Number(formData.valorAporte || 0).toFixed(1))
          : Number(formData.valorAporte || 0);

      const dataToSend = {
        nomeFundo: formData.nomeFundo.trim(),
        descricao: formData.descricao.trim(),
        valorAporte: valorAporteNormalizado,
        tipoAporte: formData.tipoAporte.trim(),
        ordem: formData.ordem === "" ? null : Number(formData.ordem),
      };

      if (editingItem) {
        await fundoReservaService.atualizarFundoReserva(editingItem.id, dataToSend);
        showSuccess("Fundo de reserva atualizado com sucesso!");
      } else {
        await fundoReservaService.cadastrarFundoReserva(dataToSend);
        showSuccess("Fundo de reserva criado com sucesso!");
      }

      await loadFundosReserva();
      handleCancel();
    } catch (err) {
      showError(err.message || "Erro ao salvar fundo de reserva");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nomeFundo: item.nomeFundo || "",
      descricao: item.descricao || "",
      valorAporte: Number(item.valorAporte || 0),
      valorAporteFormatted: formatarValorInput(item.valorAporte, item.tipoAporte),
      tipoAporte: item.tipoAporte || "",
      ordem:
        item.ordem !== null && item.ordem !== undefined ? String(Math.trunc(Number(item.ordem))) : "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir este fundo de reserva?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await fundoReservaService.deletarFundoReserva(id);
      showSuccess("Fundo de reserva excluído com sucesso!");
      await loadFundosReserva();
    } catch (err) {
      showError(err.message || "Erro ao excluir fundo de reserva");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData(FORM_INICIAL);
  };

  const handleNew = () => {
    setEditingItem(null);
    setFormData(FORM_INICIAL);
    setShowForm(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const fundosFiltrados = fundosReserva.filter((item) => {
    const termo = searchTerm.trim().toLowerCase();

    if (!termo) return true;

    return [item.nomeFundo, item.descricao, item.tipoAporte].some((campo) =>
      (campo || "").toString().toLowerCase().includes(termo)
    );
  });

  const totalRegistros = fundosFiltrados.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const fundosPaginados = fundosFiltrados.slice(indiceInicial, indiceFinal);

  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Fundos de Reserva
        </h1>
        <p className="text-gray-600">
          Gerencie os fundos de reserva e seus metadados de aporte
        </p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Pesquisar por nome, descrição ou tipo..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <button
          onClick={handleNew}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          Novo Fundo de Reserva
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">
            {editingItem ? "Editar Fundo de Reserva" : "Novo Fundo de Reserva"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="nomeFundo"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nome *
              </label>
              <input
                type="text"
                id="nomeFundo"
                name="nomeFundo"
                value={formData.nomeFundo}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite o nome do fundo"
                maxLength={100}
              />
            </div>

            <div>
              <label
                htmlFor="tipoAporte"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Tipo de aporte *
              </label>
              <select
                id="tipoAporte"
                name="tipoAporte"
                value={formData.tipoAporte}
                onChange={handleTipoAporteChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione...</option>
                {TIPOS_APORTE.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
                </select>
              </div>

            <div>
              <label
                htmlFor="valorAporte"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {isPercentual(formData.tipoAporte)
                  ? "Valor meta do aporte (%)"
                  : "Valor meta do aporte (R$)"}
              </label>
              <input
                type="text"
                id="valorAporte"
                name="valorAporte"
                value={formData.valorAporteFormatted}
                onChange={handleValorAporteChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={
                  isPercentual(formData.tipoAporte) ? "0,0" : "0,00"
                }
              />
            </div>

            <div>
              <label
                htmlFor="ordem"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Prioridade
              </label>
              <input
                type="number"
                id="ordem"
                name="ordem"
                min="0"
                step="1"
                value={formData.ordem}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex.: 1"
              />
            </div>

            <div className="md:col-span-2">
              <label
                htmlFor="descricao"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Descrição
              </label>
              <textarea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px]"
                placeholder="Descreva a finalidade do fundo"
                maxLength={500}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.nomeFundo.trim() || !formData.tipoAporte.trim()}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Save size={16} />
              {loading ? "Salvando..." : "Salvar"}
            </button>

            <button
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <X size={16} />
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center gap-4">
          <h3 className="text-lg font-semibold">Lista de Fundos de Reserva</h3>
          <span className="text-sm text-gray-500">
            {totalRegistros} registro(s) encontrado(s)
          </span>
        </div>

        {loading && fundosReserva.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando fundos de reserva...
          </div>
        ) : totalRegistros === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm
              ? "Nenhum fundo de reserva encontrado"
              : "Nenhum fundo de reserva cadastrado"}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo de aporte
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor meta
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ordem de prioridade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {fundosPaginados.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.nomeFundo}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatarTipoAporte(item.tipoAporte)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatarValorAporte(item.valorAporte, item.tipoAporte)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center">
                        {item.ordem !== null && item.ordem !== undefined
                          ? Math.trunc(Number(item.ordem))
                          : "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                        <div className="truncate" title={item.descricao || ""}>
                          {item.descricao || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                            className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={loading}
                            className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} a {Math.min(indiceFinal, totalRegistros)} de {totalRegistros} registros
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <div className="flex gap-1">
                      {gerarNumerosPaginas().map((pagina) => (
                        <button
                          key={pagina}
                          onClick={() => irParaPagina(pagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            pagina === paginacao.paginaAtual
                              ? "bg-blue-600 text-white"
                              : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {pagina}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default FundoReserva;
